/*
  Gifts

  Locks and unlocks the gifts block's items from the quantity the product form will add to
  cart, which the bundle offer or the quantity selector sets. Owns only the <product-gifts>
  element and its gift refs; it reads the product form but never writes to it.

  The block only displays the gifts. Each gift must also exist as a Shopify automatic discount.
*/
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} ProductGiftsRefs
 * @property {HTMLElement[]} [gifts]
 */

/** @extends {Component<ProductGiftsRefs>} */
class ProductGifts extends Component {
  /* No required refs: a merchant can add the block before adding any gift to it. */

  /** @type {Element | Document | null} */
  #scope = null;

  /** @type {number | undefined} */
  #frame;

  connectedCallback() {
    super.connectedCallback();
    this.#scope = this.closest('.shopify-section') ?? document;
    this.#scope.addEventListener('change', this.#scheduleUpdate);
    this.#scope.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#scheduleUpdate);
    this.#scheduleUpdate();
  }

  /** A variant change re-renders the section, which resets the gifts to their server state. */
  updatedCallback() {
    super.updatedCallback();
    this.#scheduleUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scope?.removeEventListener('change', this.#scheduleUpdate);
    this.#scope?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#scheduleUpdate);
    this.#scope = null;
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  /**
   * Waits a frame so the bundle offer, whose listeners are delegated to the document, has
   * written its quantity into the form before this block reads it.
   */
  #scheduleUpdate = () => {
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#update();
    });
  };

  /** The first quantity input in the form is the one that gets submitted. */
  get #quantity() {
    const input = this.#scope?.querySelector('product-form-component form input[name="quantity"]');
    return (input instanceof HTMLInputElement && Number(input.value)) || 1;
  }

  #update() {
    const quantity = this.#quantity;

    for (const gift of this.refs.gifts ?? []) {
      const unlockQuantity = Number(gift.dataset.unlockQuantity) || 1;
      gift.toggleAttribute('data-locked', quantity < unlockQuantity);
    }
  }
}

if (!customElements.get('product-gifts')) {
  customElements.define('product-gifts', ProductGifts);
}
